/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { AvailabilityTag, utcStringToCalendarDate, formatDateTime } from 'sarsaparilla';

import type { Availability, CarouselCardItem } from './CarouselCard.types';

interface AvailabilityBadgeProps {
    availability?: Availability;
}

export function AvailabilityBadge({ availability }: AvailabilityBadgeProps) {
    switch (availability) {
        case 'available':
            return <AvailabilityTag.Available />;
        case 'partially available':
            return <AvailabilityTag.PartiallyAvailable />;
        case 'not reservable':
            return <AvailabilityTag.NotReservable />;
        case 'lottery':
        case 'CAMPING_LOTTERY':
            return <AvailabilityTag.Lottery />;
        case 'unavailable':
            return <AvailabilityTag.Unavailable />;
        case 'new':
            return <AvailabilityTag.New />;
        case 'enter dates':
            return <AvailabilityTag.EnterDates />;
        case 'view availability':
            return <AvailabilityTag.ViewAvailability />;
        default:
            return null;
    }
}

export function getAvailabilityDatesToDisplay(item: CarouselCardItem): string {
    const startDateValue = utcStringToCalendarDate(item.available_dates?.[0]?.start_date);
    const endDateValue = utcStringToCalendarDate(item.available_dates?.[0]?.end_date);

    if (!startDateValue || !endDateValue) {
        return '';
    }

    const formatter = formatDateTime('en-US');
    const startDateMonth = formatter(startDateValue, { month: 'short' });
    const startDateDay = startDateValue.day;
    const endDateMonth = formatter(endDateValue, { month: 'short' });
    const endDateDay = endDateValue.day;

    if (startDateMonth === endDateMonth && startDateDay === endDateDay) {
        return `${startDateMonth} ${startDateDay}`;
    }

    if (startDateMonth === endDateMonth && startDateDay !== endDateDay) {
        return `${startDateMonth} ${startDateDay} - ${endDateDay}`;
    }

    return `${startDateMonth} ${startDateDay} - ${endDateMonth} ${endDateDay}`;
}
